// §4.4 — THE COMPOSER'S DEAL CONTROL (Module 09, P3-E2 PR E2-1, D-312 ruling
// 4). A quote belongs to at most one deal, and the link lives on the quote
// (`quotes.opportunity_id`). The control shows the deal a quote belongs to and
// links to it, and a holder of `sales.opportunity.write` links or unlinks one
// of the customer's open deals there (`POST /v1/quotes/{id}/opportunity`, §5).
// A composer opened from a deal (`/sales/quotes/new?customer_id=…&
// opportunity_id=…`, the deal page's Create quote) carries it into the create.
//
// WHAT THE READER IS SHOWN. `QuoteDetailResponse.opportunity` is null when the
// quote names no deal AND when the deal door would not answer this reader
// (D-312 ruling 21). A holder of `sales.opportunity.write` is also served
// `opportunity_linked` (#293), so for that reader a quote on a deal it cannot
// open reads "On a deal you cannot open" with no Link, because the link door
// would answer 409 `QUOTE_LINKED_ELSEWHERE`, and no Unlink, because the unlink
// door finds the deal through the reader's write door and answers 404. For
// anyone else "No deal" is what this reader may know. The chip links to the
// deal page for a reader of the pipeline (§7 narrowing 15) and is text for
// anyone else, because a link into a refusal is the D-088 defect.
//
// WHEN THE VERBS SHOW. Link and Unlink are deal writes, so they are offered
// to a holder of `sales.opportunity.write`, on a quote that is not finished
// (the server's four terminals, `quotes/quote-state.ts`; a finished quote's
// link is part of its deal's history and both doors answer 409
// `QUOTE_TERMINAL`), and on a desk-width screen, because §4.4's phone is
// read, approve and send only. Link lists the customer's OPEN deals this
// reader can write (`mutable`) a page at a time with Show more; a linked
// quote offers Unlink, since re-pointing needs an unlink first (§5).
// A refusal reads through the pipeline's own sentences
// (`sales.pipeline.refusal.*`, lib/pipeline.ts `refusalText`).

import { useEffect, useState } from "react";
import type { TFunction } from "i18next";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { Field } from "../../../../components/field";
import { Modal } from "../../../../components/modal";
import { Select } from "../../../../components/select";
import { RequestError } from "../../../../lib/request";
import { MutationFailureNote } from "../mutation-failure-note";
import { quoteDealChangeable, refusalText, type DealStatus } from "../lib/pipeline";
import {
  useLinkQuoteOpportunity,
  useOpportunity,
  useOpportunityPages,
  type Opportunity,
} from "../hooks/opportunity-queries";
import { canWriteOpportunities, useSalesPermissions } from "../hooks/permissions";
import { pickerView, PickerTail, usePickerCursors } from "./pipeline/deal-dialogs";
import { DealStatusChip } from "./pipeline/pipeline-parts";

/** How many of the customer's open deals one page of the Link picker reads;
 * Show more reads the next page. */
export const QUOTE_DEAL_PICKER_LIMIT = 50;

/** May this quote's deal change (pure, unit-tested)? Not once it is
 * finished: both the link and the unlink answer 409 `QUOTE_TERMINAL`. The set
 * is `lib/pipeline.ts`'s, which the deal page's Unlink and its Link picker
 * read too, so the composer and the deal can never offer different verbs. */
export { quoteDealChangeable };

/** The deal a NEW quote is created on (pure, unit-tested): the address's
 * `opportunity_id`, sent only by a holder of `sales.opportunity.write`,
 * because naming a deal is a deal write and the create refuses anyone else
 * 403 before the quote is written (§5's amended create row). */
export function dealForCreate(
  perms: { readonly has: (code: string) => boolean },
  opportunityId: string | undefined,
): string | undefined {
  return opportunityId !== undefined && canWriteOpportunities(perms) ? opportunityId : undefined;
}

/** The deal refusals a quote create can meet when it names a deal (§5's
 * amended create row), in the pipeline's words; undefined for any other
 * failure, which keeps the composer's own sentence. */
const CREATE_DEAL_REFUSALS: ReadonlySet<string> = new Set([
  "OPPORTUNITY_NOT_FOUND",
  "OPPORTUNITY_CUSTOMER_MISMATCH",
  "OPPORTUNITY_NOT_OPEN",
]);

export function createDealRefusalText(t: TFunction, error: unknown): string | undefined {
  if (!(error instanceof RequestError) || error.code === undefined) return undefined;
  return CREATE_DEAL_REFUSALS.has(error.code)
    ? refusalText(t, error, t("sales.composer.dealLinkFailed"))
    : undefined;
}

interface DealRef {
  readonly id: string;
  readonly opportunity_no: string;
  readonly title: string;
  readonly status: DealStatus;
}

function DealChip({ deal }: { readonly deal: DealRef }) {
  const { t } = useTranslation();
  const perms = useSalesPermissions();
  const label = t("sales.composer.dealChip", { no: deal.opportunity_no, title: deal.title });
  return (
    <span className="sales-toolbar" data-testid="sales-composer-deal" data-deal-id={deal.id}>
      {perms.canOpenPipeline ? (
        <Link
          to="/sales/opportunities/$opportunityId"
          params={{ opportunityId: deal.id }}
          className="ds-lnk"
          data-testid="sales-composer-deal-link-open"
        >
          {label}
        </Link>
      ) : (
        <span>{label}</span>
      )}
      {deal.status !== "open" ? <DealStatusChip status={deal.status} /> : null}
    </span>
  );
}

/** The Deal line on a SAVED quote. */
export function QuoteDealControl({
  quote,
  desktop,
}: {
  readonly quote: {
    readonly id: string;
    readonly customer_id: string;
    readonly status: string;
    readonly opportunity: DealRef | null;
    /** Whether the quote is on any deal, served to a deal writer only. */
    readonly opportunity_linked?: boolean | undefined;
  };
  readonly desktop: boolean;
}) {
  const { t } = useTranslation();
  const perms = useSalesPermissions();
  const link = useLinkQuoteOpportunity();
  const [picking, setPicking] = useState(false);
  const verbs = desktop && canWriteOpportunities(perms) && quoteDealChangeable(quote.status);
  // `?? null`: a server one deploy behind serves no `opportunity` key at all,
  // and that reads as "No deal", never as a crash.
  const deal = quote.opportunity ?? null;
  // On a deal this reader cannot open (#293): neither verb would answer.
  const hidden = deal === null && quote.opportunity_linked === true;

  return (
    <div className="sales-toolbar" data-testid="sales-composer-deal-line">
      <span className="sales-hint">{t("sales.composer.deal")}</span>
      {deal !== null ? (
        <DealChip deal={deal} />
      ) : hidden ? (
        <span data-testid="sales-composer-deal-hidden">{t("sales.composer.dealHidden")}</span>
      ) : (
        <span data-testid="sales-composer-deal-none">{t("sales.composer.dealNone")}</span>
      )}
      {verbs && deal !== null ? (
        <Button
          size="sm"
          data-testid="sales-composer-deal-unlink"
          busy={link.isPending}
          busyLabel={t("sales.composer.dealUnlinking")}
          onClick={() => {
            link.mutate({ quoteId: quote.id, opportunityId: null });
          }}
        >
          {t("sales.composer.dealUnlink")}
        </Button>
      ) : null}
      {verbs && deal === null && !hidden ? (
        <Button
          size="sm"
          data-testid="sales-composer-deal-link"
          onClick={() => {
            link.reset();
            setPicking(true);
          }}
        >
          {t("sales.composer.dealLink")}
        </Button>
      ) : null}
      {link.isError && !picking ? (
        <MutationFailureNote
          data-testid="sales-composer-deal-failed"
          text={refusalText(t, link.error, t("sales.composer.dealLinkFailed"))}
          error={link.error}
        />
      ) : null}
      {picking ? (
        <LinkDealModal
          quoteId={quote.id}
          customerId={quote.customer_id}
          onClose={() => {
            setPicking(false);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * The Link picker. It reads the customer's OPEN deals (an absent `status` is
 * open), because a link needs an open deal of the same customer (§5), a page
 * of `QUOTE_DEAL_PICKER_LIMIT` at a time with Show more (`PickerTail`, the
 * house page shape), so a customer's 51st open deal can be chosen. It offers
 * only the deals this reader can write (`mutable`), because the link door
 * answers any other 404, and a page that held none says Show more reads
 * further. A page still loading reads as a skeleton, a paused one as the
 * offline state, and a failed one as a line with its request id, never an
 * endless "Loading".
 */
function LinkDealModal({
  quoteId,
  customerId,
  onClose,
}: {
  readonly quoteId: string;
  readonly customerId: string;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const link = useLinkQuoteOpportunity();
  const walk = usePickerCursors();
  const pages = useOpportunityPages(
    { customer_id: customerId, limit: QUOTE_DEAL_PICKER_LIMIT },
    walk.cursors,
  );
  // Every walked page stays a live read (#293 review), so a deal a write
  // closed or took out of this reader's reach leaves the list wherever it sits.
  const { rows: items, read: deals } = pickerView<Opportunity>(pages);
  // Only a deal this reader's write door answers can take the link: the link
  // answers 404 `OPPORTUNITY_NOT_FOUND` for a deal it reads and cannot write
  // (§7 narrowing 16), which the list marks `mutable: false`. The walk keeps
  // folding every row, so Show more still reads on past a page of them.
  const linkable = items.filter((deal) => deal.mutable);
  const next = deals.data?.next_cursor;
  const [chosen, setChosen] = useState("");
  // A choice the list no longer offers is dropped, and Confirm never sends
  // it (the rule both deal-page pickers follow).
  const chosenOffered = linkable.some((deal) => deal.id === chosen);
  const choiceWithheld = chosen !== "" && !chosenOffered;
  useEffect(() => {
    if (choiceWithheld) setChosen("");
  }, [choiceWithheld]);
  return (
    <Modal
      heading={t("sales.composer.dealLinkTitle")}
      label={t("sales.composer.dealLinkTitle")}
      closeLabel={t("sales.common.close")}
      onClose={onClose}
      className="sales-modal"
      testId="sales-composer-deal-modal"
      pending={link.isPending}
    >
      <div className="sales-form">
        <p className="sales-hint">{t("sales.composer.dealLinkHint")}</p>
        {linkable.length === 0 ? (
          next === null ? (
            <p className="sales-hint" data-testid="sales-composer-deal-empty">
              {items.length === 0
                ? t("sales.composer.dealLinkEmpty")
                : t("sales.composer.dealLinkNoneWritable")}
            </p>
          ) : next !== undefined ? (
            <p className="sales-hint" data-testid="sales-composer-deal-none-yet">
              {t("sales.composer.dealLinkNoneYet")}
            </p>
          ) : null
        ) : (
          <Field label={t("sales.composer.deal")} wide>
            <Select
              value={chosen}
              data-testid="sales-composer-deal-pick"
              onChange={(e) => {
                link.reset();
                setChosen(e.target.value);
              }}
            >
              <option value="" disabled>
                {""}
              </option>
              {linkable.map((deal) => (
                <option key={deal.id} value={deal.id}>
                  {t("sales.composer.dealChip", { no: deal.opportunity_no, title: deal.title })}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <PickerTail
          read={deals}
          hasRows={items.length > 0}
          testPrefix="sales-composer-deal-list"
          errorText={t("sales.c360.overview.dealsError")}
          onMore={(cursor) => {
            walk.advance(cursor);
          }}
        />
      </div>
      {link.isError ? (
        <MutationFailureNote
          data-testid="sales-composer-deal-failed"
          text={refusalText(t, link.error, t("sales.composer.dealLinkFailed"))}
          error={link.error}
        />
      ) : null}
      <div className="sales-modal-actions">
        <Button onClick={onClose} disabled={link.isPending}>
          {t("sales.common.cancel")}
        </Button>
        <Button
          variant="primary"
          data-testid="sales-composer-deal-confirm"
          busy={link.isPending}
          busyLabel={t("sales.composer.dealLinking")}
          disabled={!chosenOffered}
          onClick={() => {
            if (!chosenOffered) return;
            link.mutate({ quoteId, opportunityId: chosen }, { onSuccess: onClose });
          }}
        >
          {t("sales.composer.dealLinkConfirm")}
        </Button>
      </div>
    </Modal>
  );
}

/** The Deal line on a NEW quote opened from a deal: the deal it will belong
 * to, and the sentence saying so. Shown only when the create will name it
 * (`dealForCreate`). The deal is read so its number and title can be shown;
 * until the read answers the line waits, and a deal the reader cannot read
 * still goes to the server, which answers its own refusal at the save. */
export function NewQuoteDealLine({ opportunityId }: { readonly opportunityId: string }) {
  const { t } = useTranslation();
  const deal = useOpportunity(opportunityId);
  return (
    <div className="sales-toolbar" data-testid="sales-composer-deal-line">
      <span className="sales-hint">{t("sales.composer.deal")}</span>
      {deal.data !== undefined ? (
        <DealChip
          deal={{
            id: deal.data.id,
            opportunity_no: deal.data.opportunity_no,
            title: deal.data.title,
            status: deal.data.status,
          }}
        />
      ) : null}
      <span className="sales-hint" data-testid="sales-composer-deal-from-page">
        {t("sales.composer.dealFromPage")}
      </span>
    </div>
  );
}
